import { request } from '../api/client'

/**
 * Le lot 4 vu du panneau (migration 0081) : tableau de bord, options, codes de remise,
 * demandes des clients. N'est importé que par le portail d'administration.
 */

export interface CountAmount {
  count: number
  amount: number
}

export interface PlatformDashboard {
  day: string
  mrr: number
  arr: number
  clients: number
  byStatus: Record<string, number>
  byPlan: { code: string; name: string; clients: number; mrr: number }[]
  trialsEnding: { organizationId: number; name: string; endsOn: string }[]
  renewals30: CountAmount
  outstanding: CountAmount
  overdue: CountAmount
  collectedMonth: number
  collectedYear: number
  invoicedYear: number
  churn30: number
  openRequests: number
}

export type AddonKind = 'farms' | 'users' | 'plots' | 'area'

export const ADDON_KINDS: readonly AddonKind[] = ['farms', 'users', 'plots', 'area']

export interface SubscriptionGrowth {
  limits: { maxFarms: number | null; maxUsers: number | null; maxPlots: number | null; maxAreaHectares: number | null } | null
  addons: { id: number; kind: AddonKind; quantity: number; unitPrice: number; startsOn: string }[]
  discount: { code: string; percentOff: number; cyclesRemaining: number | null; appliedOn: string } | null
}

export interface DiscountCode {
  id: number
  code: string
  description: string | null
  percentOff: number
  durationCycles: number | null
  validUntil: string | null
  maxRedemptions: number | null
  isActive: boolean
  redemptions: number
}

export type RequestKind = 'plan_change' | 'cancel' | 'addon' | 'other'

export interface ClientRequest {
  id: number
  organizationId: number
  clientName: string | null
  kind: RequestKind
  requestedPlanCode: string | null
  message: string | null
  status: 'open' | 'done' | 'declined'
  response: string | null
  createdAt: string
  handledAt: string | null
}

/** Un statut de prospect (migration 0083) : trois valeurs, et rien qu'elles (ck_lead_status). */
export type LeadStatus = 'new' | 'handled' | 'spam'

/**
 * Un prospect du site vitrine. Rendu par `core.platform_list_leads`, réservé au panneau —
 * voir la note de sûreté dans `ListLeadsHandler` côté serveur : c'est la seule barrière.
 */
export interface Lead {
  id: number
  fullName: string
  companyName: string | null
  email: string
  phone: string | null
  governorate: string | null
  message: string
  locale: string
  source: string
  status: LeadStatus
  createdAt: string
}

export const growthApi = {
  dashboard: (token: string, signal?: AbortSignal) =>
    request<PlatformDashboard>('/api/v1/platform/dashboard', { token, signal }),

  clientGrowth: (token: string, organizationId: number, signal?: AbortSignal) =>
    request<SubscriptionGrowth>(`/api/v1/platform/clients/${organizationId}/growth`, { token, signal }),

  addAddon: (token: string, organizationId: number, body: { kind: AddonKind; quantity: number; unitPrice: number | null }) =>
    request<{ id: number }>(`/api/v1/platform/clients/${organizationId}/addons`, { method: 'POST', token, body }),

  endAddon: (token: string, addonId: number, reason: string) =>
    request<void>(`/api/v1/platform/addons/${addonId}/end`, { method: 'POST', token, body: { reason } }),

  listCodes: (token: string, signal?: AbortSignal) =>
    request<DiscountCode[]>('/api/v1/platform/discount-codes', { token, signal }),

  saveCode: (
    token: string,
    body: { code: string; description: string | null; percentOff: number; durationCycles: number | null; validUntil: string | null; maxRedemptions: number | null; isActive: boolean },
  ) => request<{ id: number }>('/api/v1/platform/discount-codes', { method: 'POST', token, body }),

  applyDiscount: (token: string, organizationId: number, code: string) =>
    request<void>(`/api/v1/platform/clients/${organizationId}/discount`, { method: 'POST', token, body: { code } }),

  removeDiscount: (token: string, organizationId: number) =>
    request<{ changed: boolean }>(`/api/v1/platform/clients/${organizationId}/discount`, { method: 'DELETE', token }),

  listRequests: (token: string, status: string | null, signal?: AbortSignal) =>
    request<ClientRequest[]>(status === null ? '/api/v1/platform/requests' : `/api/v1/platform/requests?status=${status}`, { token, signal }),

  handleRequest: (token: string, id: number, status: 'done' | 'declined', response: string | null) =>
    request<void>(`/api/v1/platform/requests/${id}/handle`, { method: 'POST', token, body: { status, response } }),

  listLeads: (token: string, status: LeadStatus | null, limit: number, signal?: AbortSignal) =>
    request<Lead[]>(
      status === null ? `/api/v1/platform/leads?limit=${limit}` : `/api/v1/platform/leads?status=${status}&limit=${limit}`,
      { token, signal },
    ),

  setLeadStatus: (token: string, id: number, status: LeadStatus) =>
    request<void>(`/api/v1/platform/leads/${id}/status`, { method: 'POST', token, body: { status } }),
}
